import firebase from 'firebase'
import axios from 'axios'
import moment from 'moment'
import { copyscapeHTTP } from '@/api/http'
import { parseString } from 'xml2js'

export default {
  state: {
    content: null,
    chatterbox: null,
    linkbuilding: null,
    contentTypes: [
      {
        name: 'SEO',
        type: 'content',
        hasMoreContent: true,
      },
      {
        name: 'Review',
        type: 'content',
        hasMoreContent: true,
      },
      {
        name: 'LinkBuilding',
        type: 'linkbuilding',
        hasMoreContent: true,
      },
      {
        name: 'Chatterbox',
        type: 'chatterbox',
        hasMoreContent: true,
      },
    ],
    userContent: {},
    lastItemInResponse: {},
  },
  actions: {
    fetchUserContent: async ({ commit, state, rootState }, filters = {}) => {
      try {
        const userId = rootState.user.user.data.uid
        let payload = {}

        for await (const contentType of state.contentTypes) {
          let contentCollection = await queryCollection(state, userId, contentType.type, contentType.name, filters)
          mapContent(contentCollection, payload)
        }
        commitMutations(commit, payload)

        return payload
      } catch (error) {
        console.error('Error fetching user data from firebase: ', error)
      }
    },
    fetchMoreContent: async ({ commit, state, rootState }, { contentTypeName, filters }) => {
      try {
        const userId = rootState.user.user.data.uid
        const contentType = state.contentTypes.find(type => type.name === contentTypeName)

        let contentCollection = await queryCollection(state, userId, contentType.type, contentType.name, filters, true)

        if (contentCollection.empty) return commit('SET_ITEM_AS_LAST', contentTypeName)

        mapContent(contentCollection, state.userContent)
        commitMutations(commit, state.userContent)
      } catch (err) {
        console.error('Error fetching user data from firebase: ', err)
      }
    },
    fetchContentById: async ({ commit, dispatch, state, rootState }, payload) => {
      const { user } = rootState.user
      const { collection, documentId } = payload

      const document = await firebase
        .firestore()
        .collection('users')
        .doc(user.data.uid)
        .collection(collection)
        .doc(documentId)
        .get()
      commit('SET_DOCUMENT', { collection, data: document.data() })
    },
    createContent: async ({ state, rootState }, payload) => {
      const { collection, newDocument } = payload
      const { user } = rootState.user

      newDocument.createdAt = new Date().toISOString()
      return await firebase
        .firestore()
        .collection('users')
        .doc(user.data.uid)
        .collection(collection)
        .add(newDocument)
    },
    publishContentById: async ({ commit, dispatch, state, rootState }, payload) => {
      const { user } = rootState.user
      const { collection, documentId } = payload

      const copyscapeScore = await dispatch('checkCopyscapeScore', collection)

      state[collection].status = 'Pending Review'

      const batch = firebase.firestore().batch()
      const userContentRef = firebase
        .firestore()
        .collection('users')
        .doc(user.data.uid)
        .collection(collection)
        .doc(documentId)

      await batch.set(userContentRef, state[collection])

      const adminData = {}
      adminData.submittedAt = new Date().toISOString()
      adminData.submittedBy = user.data.uid
      adminData.status = state[collection].status

      if (collection === 'content') adminData.contentId = documentId
      if (collection === 'linkbuilding') {
        adminData.linkbuildingId = documentId
        adminData.url = ''
      }
      if (collection === 'chatterbox') adminData.chatterboxId = documentId

      if (copyscapeScore != null) {
        adminData.copyscapeScore = copyscapeScore
      }

      const collectionRef = firebase
        .firestore()
        .collection(collection)
        .doc()

      await batch.set(collectionRef, { userData: state[collection], adminData })
      await batch.commit()

      if (process.env.NODE_ENV === 'production') {
        await dispatch('sendTeamsNotification', collection)
      }

      return state[collection]
    },
    saveContentById: async ({ commit, state, rootState }, payload) => {
      const { user } = rootState.user
      const { collection, documentId } = payload

      state[collection].status = 'Draft'
      return await firebase
        .firestore()
        .collection('users')
        .doc(user.data.uid)
        .collection(collection)
        .doc(documentId)
        .set(state[collection])
    },
    deleteContentById: async ({ rootState, commit, dispatch }, payload) => {
      const { user } = rootState.user
      const { collection, documentId } = payload

      return await firebase
        .firestore()
        .collection('users')
        .doc(user.data.uid)
        .collection(collection)
        .doc(documentId)
        .delete()
    },
    checkCopyscapeScore: async ({ state }, collection) => {
      const bodyFormData = new FormData()
      bodyFormData.set('t', state[collection].content)

      let { data } = await copyscapeHTTP.post('', bodyFormData, { headers: { 'Content-Type': 'multipart/form-data' } })
      let copyscapeResponse = await parseXMLString(data)
      return copyscapeResponse.response.allpercentmatched ? copyscapeResponse.response.allpercentmatched[0] : null
    },
    sendTeamsNotification: async ({ state, rootState }, collection) => {
      const { content } = state
      const { user } = rootState.user
      const baseUrl = 'https://i4prbzl43a.execute-api.eu-central-1.amazonaws.com/prod/?'

      let params = getCollectionParams(collection, state[collection], user)

      const url =
        baseUrl +
        Object.keys(params)
          .map(key => key + '=' + params[key])
          .join('&')

      return await axios.get(url)
    },
  },
  mutations: {
    SET_USER_CONTENT(state, payload) {
      state.userContent = payload
    },
    SET_LAST_ITEM_IN_RESPONSE(state, payload) {
      Object.keys(payload).map(content => {
        let itemsLength = payload[content].length
        state.lastItemInResponse[content] = payload[content][itemsLength - 1]
      })
    },
    SET_ITEM_AS_LAST(state, payload) {
      let contentLength = state.userContent[payload].length
      state.userContent[payload][contentLength - 1].isLast = true
    },
    SET_DOCUMENT(state, payload) {
      const { collection, data } = payload
      state[collection] = data
    },
  },
  getters: {
    content(state) {
      return state.content
    },
    linkbuilding(state) {
      return state.linkbuilding
    },
    chatterbox(state) {
      return state.chatterbox
    },
    userContent(state) {
      return state.userContent
    },
    contentTypes(state) {
      return state.contentTypes
    },
  },
}

/** Helper functions */

function commitMutations(commit, payload) {
  commit('SET_USER_CONTENT', payload)

  if (Object.keys(payload).length) {
    commit('SET_LAST_ITEM_IN_RESPONSE', payload)
  }
}

function getCollectionParams(collection, document, user) {
  let params = {}
  if (collection === 'content') {
    params = {
      site: document.site.name,
      language: document.language.id,
      type: document.type,
      keyword: document.keyword,
      brand: document.brand.name,
      title: document.title,
      name: user.data.firestore.name,
    }
  }

  if (collection === 'linkbuilding') {
    params = {
      type: document.type,
      sites: document.siteIds.toString(),
      language: document.languageId,
      url: document.url,
      submittedBy: user.data.firestore.name,
    }
  }

  if (collection === 'chatterbox') {
    params = {
      type: document.type,
      site: document.siteId,
      language: document.languageId,
      url: document.url,
      submittedBy: user.data.firestore.name,
    }
  }

  return params
}

/**
 * Query and paginate Firestore collection
 *
 * @param   {Object}  state       module state object
 * @param   {Number}  userId      user unique id
 * @param   {String}  collection  collection name
 * @param   {String}  type        collection type
 * @param   {Number}  pagination  number of items to retrive
 * @param   {Boolean} startAfter  If the collection should be paginated
 *
 * @return  {Object}              Firestore collection
 */
async function queryCollection(state, userId, collection, type, filters, startAfter = false) {
  const pagination = 5
  const lastItem = state.lastItemInResponse[type]
  let statusFilter,
    monthFilter = null

  if (Object.keys(filters).length) {
    statusFilter = filters.statusFilter
    monthFilter = filters.monthFilter
  }

  let query = firebase
    .firestore()
    .collection('users')
    .doc(userId)
    .collection(collection)
    .where('type', '==', type)

  if (statusFilter && statusFilter != '') {
    query = query.where('status', '==', statusFilter)
  }

  if (monthFilter != null && monthFilter != -1) {
    const startDate = moment([moment().year(), monthFilter])
    const endDate = moment(startDate).endOf('month')
    query = query.where('createdAt', '>=', startDate.toISOString()).where('createdAt', '<=', endDate.toISOString())
  }

  if (startAfter) {
    query = query.orderBy('createdAt', 'desc').startAfter(lastItem.createdAt)
  } else {
    query = query.orderBy('createdAt', 'desc')
  }

  return await query.limit(pagination).get()
}

/**
 * Map firestore collection
 *
 * @param   {Object}  seoContent     seoContent firestore collection
 * @param   {Object}  reviewContent  reviewContent firestore collection
 * @param   {Object}  linksContent   linksContent firestore collection
 * @param   {Object}  payloadObject  payloadObject object to store user content
 *
 * @return  {Object}                 payloadObject
 */
async function mapContent(collection, payloadObject) {
  collection.forEach(doc => {
    const type = doc.data()['type']
    let title = type === 'Review' ? `${doc.data().brand.name} Review` : doc.data()['title']
    let path = '/content/'

    if (!payloadObject[type]) payloadObject[type] = []

    if (type === 'LinkBuilding') {
      title = doc.data()['anchors'][0]['text']
      path = '/linkbuilding/'
    }
    if (type === 'Chatterbox') {
      title = doc.data()['url']
      path = '/chatterbox/'
    }

    payloadObject[type].push({
      id: doc.id,
      path,
      title,
      type,
      createdAt: doc.data()['createdAt'],
      status: doc.data()['status'],
      isLast: collection.docs.length < 5,
    })
  })
  return payloadObject
}

/**
 * Convert XML String to JSON
 */
const parseXMLString = string => {
  return new Promise((resolve, reject) => {
    parseString(string, (err, data) => {
      if (err) {
        return reject(err)
      }
      resolve(data)
    })
  })
}
